import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  ShoppingBagIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  EyeIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    totalCustomers: 0,
    totalProducts: 0,
    revenueChange: 0,
    ordersChange: 0,
    customersChange: 0,
    productsChange: 0
  });

  const [recentOrders, setRecentOrders] = useState([]);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all dashboard data in parallel - using REAL backend APIs only
      const [
        statsRes,
        ordersRes,
        customersRes,
        lowStockRes
      ] = await Promise.all([
        axios.get('/api/admin/dashboard/enhanced-stats'),
        axios.get('/api/admin/orders/recent'),
        axios.get('/api/admin/customers/recent'),
        axios.get('/api/admin/products/low-stock')
      ]);

      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.orders || []);
      setRecentCustomers(customersRes.data.customers || []);
      setLowStockProducts(lowStockRes.data.products || []);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Show error instead of mock data - we want to catch real issues
      setStats({
        totalRevenue: 0,
        totalOrders: 0,
        totalCustomers: 0,
        totalProducts: 0,
        revenueChange: 0,
        ordersChange: 0,
        customersChange: 0,
        productsChange: 0
      });
    } finally {
      setLoading(false);
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const StatCard = ({ title, value, change, icon: Icon, color, href }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          <div className="flex items-center mt-2">
            {change >= 0 ? (
              <ArrowUpIcon className="h-4 w-4 text-green-500 ml-1" />
            ) : (
              <ArrowDownIcon className="h-4 w-4 text-red-500 ml-1" />
            )}
            <span className={`text-sm font-medium ${
              change >= 0 ? 'text-green-500' : 'text-red-500'
            }`}>
              {Math.abs(change)}%
            </span>
            <span className="text-sm text-gray-500 mr-2">מהחודש הקודם</span>
          </div>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
      </div>
      {href && (
        <div className="mt-4">
          <Link
            to={href}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            צפה בפרטים ←
          </Link>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">דשבורד ניהול</h1>
          <p className="text-gray-600 mt-2">סקירה כוללת של המערכת והפעילות</p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          <Link
            to="/admin/products"
            className="btn btn-primary flex items-center"
          >
            <PlusIcon className="h-4 w-4 ml-2" />
            הוסף מוצר
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="סך הכנסות"
          value={formatCurrency(stats.totalRevenue)}
          change={stats.revenueChange}
          icon={CurrencyDollarIcon}
          color="bg-green-500"
          href="/admin/reports/revenue"
        />
        <StatCard
          title="הזמנות"
          value={stats.totalOrders.toLocaleString('he-IL')}
          change={stats.ordersChange}
          icon={ShoppingBagIcon}
          color="bg-blue-500"
          href="/admin/orders"
        />
        <StatCard
          title="לקוחות"
          value={stats.totalCustomers.toLocaleString('he-IL')}
          change={stats.customersChange}
          icon={UsersIcon}
          color="bg-purple-500"
          href="/admin/customers"
        />
        <StatCard
          title="מוצרים"
          value={stats.totalProducts.toLocaleString('he-IL')}
          change={stats.productsChange}
          icon={ChartBarIcon}
          color="bg-orange-500"
          href="/admin/products"
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Recent Orders */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">הזמנות אחרונות</h3>
              <Link
                to="/admin/orders"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
              >
                צפה בכל ההזמנות
                <EyeIcon className="h-4 w-4 mr-1" />
              </Link>
            </div>
          </div>
          <div className="p-6">
            {recentOrders.length > 0 ? (
              <div className="space-y-4">
                {recentOrders.slice(0, 5).map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="font-semibold text-gray-900">{order.order_number}</p>
                      <p className="text-sm text-gray-600">{order.customer_name}</p>
                      <p className="text-xs text-gray-500">{formatDate(order.createdAt)}</p>
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">{formatCurrency(order.total_amount)}</p>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${order.statusColor}`}>
                        {order.statusLabel}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ShoppingBagIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">אין הזמנות אחרונות</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Customers */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">לקוחות חדשים</h3>
              <Link
                to="/admin/customers"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
              >
                צפה בכל הלקוחות
                <EyeIcon className="h-4 w-4 mr-1" />
              </Link>
            </div>
          </div>
          <div className="p-6">
            {recentCustomers.length > 0 ? (
              <div className="space-y-4">
                {recentCustomers.slice(0, 5).map((customer) => (
                  <div key={customer.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold">
                          {customer.first_name.charAt(0)}
                        </span>
                      </div>
                      <div className="mr-3">
                        <p className="font-semibold text-gray-900">{customer.first_name} {customer.last_name}</p>
                        <p className="text-sm text-gray-600">{customer.email}</p>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          customer.type === 'b2b' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {customer.typeLabel}
                        </span>
                      </div>
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-gray-500">{formatDate(customer.createdAt)}</p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        פעיל
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">אין לקוחות חדשים</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Alerts and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Low Stock Alert */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 ml-2" />
              <h3 className="text-lg font-semibold text-gray-900">התראות מלאי נמוך</h3>
            </div>
          </div>
          <div className="p-6">
            {lowStockProducts.length > 0 ? (
              <div className="space-y-3">
                {lowStockProducts.slice(0, 5).map((product) => (
                  <div key={product.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors">
                    <div>
                      <p className="font-semibold text-gray-900">{product.name}</p>
                      <p className="text-sm text-amber-600">נותרו {product.stock_quantity} יחידות</p>
                      <p className="text-xs text-gray-500">SKU: {product.sku}</p>
                    </div>
                    <Link
                      to="/admin/products"
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      עדכן מלאי
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">כל המוצרים במלאי תקין</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">פעולות מהירות</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <Link
                to="/admin/products"
                className="flex flex-col items-center p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <PlusIcon className="h-8 w-8 text-blue-600 mb-2" />
                <span className="text-sm font-medium text-blue-900">הוסף מוצר</span>
              </Link>
              
              <Link
                to="/admin/orders"
                className="flex flex-col items-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
              >
                <ClockIcon className="h-8 w-8 text-yellow-600 mb-2" />
                <span className="text-sm font-medium text-yellow-900">הזמנות בהמתנה</span>
              </Link>
              
              <Link
                to="/admin/price-negotiations"
                className="flex flex-col items-center p-4 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <svg className="h-8 w-8 text-purple-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="text-sm font-medium text-purple-900">משא ומתן</span>
              </Link>
              
              <Link
                to="/admin/customers"
                className="flex flex-col items-center p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                <UsersIcon className="h-8 w-8 text-green-600 mb-2" />
                <span className="text-sm font-medium text-green-900">הוסף לקוח</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
